import {Trans} from '@lingui/macro';
import {DateTime} from 'luxon';
import React, {FC} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {AsyncHandlerOf, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {SmartDateTimeFormatterContainer} from '../../../../srcCommon/formatters/dateTime/smartDateTimeFormatterContainer';
import {getValidTimestampFromValue} from '../../../helpers/dateTimeHelpers';
import {buildTeammateDisplayName} from '../../../helpers/modelHelpers';
import {
  addCustomAttribute,
  CardAttributeUpdateAction,
  deleteCustomAttribute,
  updateCustomAttribute,
} from '../../../helpers/models/cardAttributeUpdateHelpers';
import {ContactDetailsSectionWithConflictsEnum} from '../../../helpers/models/cardMergeHelpers';
import {CustomFieldModel, CustomFieldsEnum} from '../../../models/api/customFieldModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {LinkifyFormatter} from '../../common/formatters/markdown/linkifyFormatter';
import {InlineEditingBooleanPopover} from '../../common/inlineEditing/inlineEditingBooleanPopover';
import {InlineEditingDateTimePickerContainer} from '../../common/inlineEditing/inlineEditingDateTimePickerContainer';
import {InlineEditingInboxPopover} from '../../common/inlineEditing/inlineEditingInboxPopover';
import {InlineEditingNumberInputBox} from '../../common/inlineEditing/inlineEditingNumberInputBox';
import {InlineEditingTeammatePopover} from '../../common/inlineEditing/inlineEditingTeammatePopover';
import {InlineEditingTextBox} from '../../common/inlineEditing/inlineEditingTextBox';
import {InlineEditingProps} from '../../common/inlineEditing/useInlineEditing';
import {LinkTrap} from '../../common/linkTrap/linkTrap';
import {useContactDetailsContext} from '../contactDetailsContext';
import {ContactMergeConflictingValue} from '../contactMerge/contactMergeConflictingValue';
import {ContactInformationAttribute} from './contactInformationAttribute';
import {CustomAttributeInboxContainer} from './customAttributeInboxContainer';
import {CustomAttributeTeammateContainer} from './customAttributeTeammateContainer';

/*
 * Props.
 */

export interface CustomAttributeModel {
  customField: CustomFieldModel;
  value: string;
}

interface CustomAttributeProps {
  customAttribute: CustomAttributeModel;
  teammates: ReadonlyArray<TeammateModel>;
  updateCardAttributeAsync: AsyncHandlerOf<CardAttributeUpdateAction>;
  updateUnsavedCard: HandlerOf<CardAttributeUpdateAction>;
}

/*
 * Style.
 */

const StyledValueDiv = styled.div`
  margin: 8px 0 7px 0;
`;

const StyledDatetimeValueDiv = styled.div`
  display: flex;
`;

const StyledCalendarIcon = styled(Icon)`
  margin-right: 8px;
`;

const StyledMergeConflictTeammateContainerDiv = styled.div`
  margin-top: 2px;
`;

const StyledContactMergeContainerDiv = styled.div`
  flex: 1;
  margin: 4px -4px 7px 0;
  overflow: hidden;
`;

const StyledContentDiv = styled.div`
  margin: 10px 0;
`;

const StyledTitleDiv = styled.div`
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.bold};
`;

/*
 * Components.
 */

export const CustomAttribute: FC<CustomAttributeProps> = (props) => {
  const {customAttribute, teammates, updateCardAttributeAsync, updateUnsavedCard} = props;
  const isAddingNewCustomAttribute = customAttribute.value === '';
  const customFieldId = customAttribute.customField.id;

  const contactDetailsContext = useContactDetailsContext();

  // Check if we are merging and have a merge conflict
  const conflictingCustomAttributes =
    contactDetailsContext.mergeContext?.conflictingMergeValues?.[
      ContactDetailsSectionWithConflictsEnum.CONTACT_INFORMATION
    ];
  const shouldShowMergeConflict = Boolean(
    conflictingCustomAttributes && conflictingCustomAttributes.some((value) => value.id === customFieldId),
  );

  if (shouldShowMergeConflict) {
    return (
      <StyledContentDiv {...buildInteractionIdProps(`contact-details-custom-attribute-${customFieldId}`)}>
        <StyledTitleDiv>{customAttribute.customField.name}</StyledTitleDiv>
        <StyledContactMergeContainerDiv>
          <ContactMergeConflictingValue
            section={ContactDetailsSectionWithConflictsEnum.CONTACT_INFORMATION}
            customFieldId={customFieldId}
            renderOption={(value) => {
              if (typeof value !== 'string') {
                return value;
              }
              if (customAttribute.customField.type === CustomFieldsEnum.STRING) {
                return value;
              }
              if (customAttribute.customField.type === CustomFieldsEnum.TEAMMATE) {
                return (
                  <StyledMergeConflictTeammateContainerDiv>
                    {renderCustomAttributeValue({...customAttribute, value})}
                  </StyledMergeConflictTeammateContainerDiv>
                );
              }
              return renderCustomAttributeValue({...customAttribute, value});
            }}
          />
        </StyledContactMergeContainerDiv>
      </StyledContentDiv>
    );
  }
  return (
    <ContactInformationAttribute
      key={customFieldId + customAttribute.value}
      interactionId={`contact-details-custom-attribute-${customFieldId}`}
      title={customAttribute.customField.name}
      currentAttributeValue={customAttribute.value}
      renderValue={(inlineEditingProps) => {
        if (inlineEditingProps.isEditing) {
          return renderInlineEditingInput(customAttribute, inlineEditingProps);
        }

        if (isAddingNewCustomAttribute) {
          return <StyledValueDiv>-</StyledValueDiv>;
        }

        return <StyledValueDiv>{renderCustomAttributeValue(customAttribute)}</StyledValueDiv>;
      }}
      copyValue={generateCopyValue(customAttribute, teammates)}
      onSave={(newValue: string) => {
        const action = computeOnSaveAction(
          isAddingNewCustomAttribute,
          customAttribute.value,
          customAttribute.customField,
          newValue,
        );
        return updateCardAttributeAsync(action);
      }}
      onDelete={() => {
        const action = deleteCustomAttribute(customAttribute.value, customAttribute.customField);

        // No need to hit the backend if the user deletes the attribute before it's saved
        if (isAddingNewCustomAttribute) {
          return updateUnsavedCard(action);
        }

        return updateCardAttributeAsync(action);
      }}
      shouldStartInEditMode={isAddingNewCustomAttribute}
      shouldHideCopyOption={customAttribute.customField.type === CustomFieldsEnum.BOOLEAN}
    />
  );
};

function renderCustomAttributeValue(customAttribute: CustomAttributeModel) {
  const {value, customField} = customAttribute;
  switch (customField.type) {
    case CustomFieldsEnum.STRING:
      // User sometimes enter links - this lets those links be clickable.
      return (
        <LinkTrap>
          <LinkifyFormatter text={value} />
        </LinkTrap>
      );

    case CustomFieldsEnum.DATETIME:
      return (
        <UserThemeConsumer>
          {(theme) => (
            <StyledDatetimeValueDiv>
              <StyledCalendarIcon name="calendarSmall" color={theme.greys.shade70} />
              <SmartDateTimeFormatterContainer value={getValidTimestampFromValue(value)} />
            </StyledDatetimeValueDiv>
          )}
        </UserThemeConsumer>
      );

    case CustomFieldsEnum.BOOLEAN:
      return value === 'true' ? (
        <Trans id="contact-details-custom-attribute-value-yes">Yes</Trans>
      ) : (
        <Trans id="contact-details-custom-attribute-value-no">No</Trans>
      );

    case CustomFieldsEnum.TEAMMATE:
      return <CustomAttributeTeammateContainer teammateEmail={customAttribute.value} />;

    case CustomFieldsEnum.INBOX:
      return <CustomAttributeInboxContainer inboxPublicId={customAttribute.value} />;

    default:
      return value;
  }
}

function renderInlineEditingInput(
  customAttribute: CustomAttributeModel,
  inlineEditingProps: InlineEditingProps,
) {
  const {customField} = customAttribute;
  const {editedValue, onEdit, onEditSave, onEditCancel} = inlineEditingProps;

  switch (customField.type) {
    case CustomFieldsEnum.STRING:
      return (
        <InlineEditingTextBox
          value={editedValue}
          onEdit={onEdit}
          onEditSave={onEditSave}
          onEditCancel={onEditCancel}
        />
      );

    case CustomFieldsEnum.DATETIME:
      return (
        <InlineEditingDateTimePickerContainer
          value={editedValue}
          onEdit={onEdit}
          onEditSave={onEditSave}
          selectedValue={customAttribute.value}
        />
      );

    case CustomFieldsEnum.BOOLEAN:
      return (
        <InlineEditingBooleanPopover
          onEditSave={onEditSave}
          onEditCancel={onEditCancel}
          value={customAttribute.value}
        />
      );

    case CustomFieldsEnum.NUMBER:
      return (
        <InlineEditingNumberInputBox
          value={editedValue}
          onEdit={onEdit}
          onEditSave={onEditSave}
          onEditCancel={onEditCancel}
        />
      );

    case CustomFieldsEnum.TEAMMATE:
      return (
        <InlineEditingTeammatePopover
          onEditSave={(teammate: TeammateModel) => onEditSave(teammate.email)}
          onEditCancel={onEditCancel}
          teammateEmail={customAttribute.value}
          renderButtonContent={(teammateEmail: string) => (
            <CustomAttributeTeammateContainer teammateEmail={teammateEmail} />
          )}
        />
      );

    case CustomFieldsEnum.INBOX:
      return (
        <InlineEditingInboxPopover
          onEditSave={onEditSave}
          onEditCancel={onEditCancel}
          inboxPublicId={customAttribute.value}
          renderButtonContent={(inboxPublicId: string) => (
            <CustomAttributeInboxContainer inboxPublicId={inboxPublicId} />
          )}
        />
      );

    default:
      return null;
  }
}

function computeOnSaveAction(
  isAddingNewCustomAttribute: boolean,
  currentValue: string,
  customField: CustomFieldModel,
  newValue: string,
) {
  if (isAddingNewCustomAttribute) {
    return addCustomAttribute(newValue, customField);
  }

  // If the user empties the field, treat it as a delete
  if (!newValue) {
    return deleteCustomAttribute(currentValue, customField);
  }

  return updateCustomAttribute(currentValue, newValue, customField);
}

function generateCopyValue(customAttribute: CustomAttributeModel, teammates: ReadonlyArray<TeammateModel>) {
  const {value, customField} = customAttribute;
  switch (customField.type) {
    case CustomFieldsEnum.DATETIME:
      return DateTime.fromMillis(Number(value)).toISO();

    case CustomFieldsEnum.TEAMMATE: {
      const teammate = teammates.find((tm) => tm.email === value);
      if (!teammate) {
        return value;
      }
      return `${buildTeammateDisplayName(teammate)} (${value})`;
    }

    default:
      return value;
  }
}
